import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

import { deriveRunbookRefAlias } from './derive-alias'

/**
 * Visual pill for an `@scope.path` ref. Color-coded by scope to match
 * RFC-167-MOCKUP.html: amber for `@params`, neutral grey for `@step`,
 * neutral for `@run` / `@env`. Stores the canonical ref but renders the
 * friendly alias derived via `deriveRunbookRefAlias`.
 *
 * Click selects the node (mirrors ActionPill) and emits an
 * `openRunbookRefMentionEditor` event so consumers can attach a popover.
 */
export function RunbookRefMentionNodeView(props: NodeViewProps) {
  const ref = (props.node.attrs.ref as string) || ''
  const alias = deriveRunbookRefAlias(ref)
  const { editor, getPos } = props

  const handleClick = () => {
    if (typeof getPos !== 'function') return
    const pos = getPos()
    if (typeof pos !== 'number') return
    try {
      editor.commands.setNodeSelection(pos)
    } catch {
      // selectNodeAt may throw if pos is out of range during a remount;
      // ignore and let the click bubble.
    }
    // @ts-expect-error — custom editor event reused by surrounding UI.
    editor.emit('openRunbookRefMentionEditor', { pos, ref })
  }

  return (
    <NodeViewWrapper as='span' className='runbook-ref-mention inline'>
      <span
        className='bg-primary text-primary-foreground inline-flex h-[24px] cursor-pointer items-center gap-0 rounded-sm px-2 align-middle text-[11px] leading-none font-["Inter",system-ui,sans-serif] font-semibold whitespace-nowrap select-none'
        data-runbook-ref=''
        data-ref={ref}
        title={ref}
        contentEditable={false}
        onClick={handleClick}
      >
        <span>@</span>
        <span>{alias}</span>
      </span>
    </NodeViewWrapper>
  )
}
